'use client'

import { useEffect, useState, useCallback } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { pluralPl } from '@/lib/format'
import { submitReview, deleteOwnReview } from './actions'

export interface ReviewItem {
  id: string
  author: string
  rating: number
  comment: string | null
  createdAt: string // ISO
}

export interface ReviewsProps {
  productId: string
  categorySlug: string
  slug: string
  reviews: ReviewItem[]
  stats: { count: number; avg: number }
  unavailable?: boolean
}

const STAR_PATH =
  'M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14l-5-4.87 7.1-1.01L12 2z'

function Stars({ value, size = 16 }: { value: number; size?: number }) {
  return (
    <>
      <span className="inline-flex" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((i) => (
          <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={i <= value ? '#F59E0B' : 'rgba(255,255,255,0.18)'}>
            <path d={STAR_PATH} />
          </svg>
        ))}
      </span>
      {/* Tekstowa alternatywa dla czytników ekranu — same SVG są aria-hidden. */}
      <span className="sr-only">Ocena: {value}/5</span>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })
}

// Stan zalogowania + własna opinia dociągane klientowo, żeby strona produktu
// pozostała statyczna (ISR). Lista opinii przychodzi serwerowo w `reviews`.
export function Reviews({ productId, categorySlug, slug, reviews, stats, unavailable }: ReviewsProps) {
  const router = useRouter()
  const [loggedIn, setLoggedIn] = useState<boolean | null>(null)
  const [ownReviewId, setOwnReviewId] = useState<string | null>(null)
  const [rating, setRating] = useState(0)
  const [hover, setHover] = useState(0)
  const [comment, setComment] = useState('')
  const [pending, setPending] = useState(false)
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)

  const loadMine = useCallback(async () => {
    try {
      const res = await fetch(`/api/me/review?productId=${encodeURIComponent(productId)}`, { cache: 'no-store' })
      const data = (await res.json()) as { loggedIn: boolean; review: { id: string; rating: number; comment: string | null } | null; unavailable?: boolean }
      if (data.unavailable) {
        setLoggedIn(false)
        return
      }
      setLoggedIn(data.loggedIn)
      setOwnReviewId(data.review?.id ?? null)
      if (data.review) {
        setRating(data.review.rating)
        setComment(data.review.comment ?? '')
      }
    } catch {
      setLoggedIn(false)
    }
  }, [productId])

  // Dociągamy stan zalogowania po zamontowaniu (fetch on-mount). setState dzieje się
  // dopiero po await w loadMine — nie synchronicznie — więc kaskada renderów nie grozi.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- asynchroniczny fetch on-mount, setState po await
    void loadMine()
  }, [loadMine])

  const editing = Boolean(ownReviewId)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (rating < 1) {
      setMsg({ type: 'err', text: 'Wybierz ocenę (1–5 gwiazdek).' })
      return
    }
    setPending(true)
    setMsg(null)
    const res = await submitReview({ productId, categorySlug, slug, rating, comment })
    setPending(false)
    if (res.ok) {
      setMsg({ type: 'ok', text: 'Dziękujemy! Twoja opinia została zapisana.' })
      router.refresh()
      loadMine()
    } else {
      setMsg({
        type: 'err',
        text:
          res.error === 'auth'
            ? 'Musisz być zalogowany, aby dodać opinię.'
            : res.error === 'rating'
              ? 'Nieprawidłowa ocena.'
              : res.error === 'unavailable'
                ? 'Opinie są chwilowo niedostępne. Spróbuj ponownie.'
                : 'Coś poszło nie tak. Spróbuj ponownie.',
      })
    }
  }

  async function handleDelete() {
    setPending(true)
    setMsg(null)
    const res = await deleteOwnReview({ productId, categorySlug, slug })
    setPending(false)
    // Przy porażce (np. wygasła sesja) nie czyścimy stanu — pokazujemy komunikat.
    if (!res.ok) {
      setMsg({ type: 'err', text: 'Nie udało się usunąć opinii. Spróbuj ponownie.' })
      return
    }
    setOwnReviewId(null)
    setRating(0)
    setComment('')
    router.refresh()
  }

  return (
    <div>
      {/* Podsumowanie ocen */}
      <div className="flex items-center gap-4 mb-8">
        {stats.count > 0 ? (
          <>
            <span className="text-4xl font-bold text-white">{stats.avg.toFixed(1).replace('.', ',')}</span>
            <div>
              <Stars value={Math.round(stats.avg)} size={18} />
              <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {stats.count} {pluralPl(stats.count, ['opinia', 'opinie', 'opinii'])}
              </p>
            </div>
          </>
        ) : (
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Brak opinii — bądź pierwszy, który oceni ten produkt.
          </p>
        )}
      </div>

      {unavailable && (
        <p className="text-sm mb-8" style={{ color: '#F87171' }}>
          Opinie są chwilowo niedostępne. Spróbuj odświeżyć stronę za chwilę.
        </p>
      )}

      {/* Formularz — tylko zalogowani. Dopóki nie wiemy (null), nic nie pokazujemy. */}
      {!unavailable && loggedIn === true && (
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl p-6 mb-8"
          style={{ backgroundColor: '#0D1E30', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <p className="font-heading font-semibold text-white mb-3">{editing ? 'Twoja opinia' : 'Dodaj opinię'}</p>

          <div className="flex items-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <button
                key={i}
                type="button"
                onClick={() => setRating(i)}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(0)}
                aria-label={`Oceń na ${i} ${pluralPl(i, ['gwiazdkę', 'gwiazdki', 'gwiazdek'])}`}
                aria-pressed={rating === i}
                className="transition-transform hover:scale-110"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill={i <= (hover || rating) ? '#F59E0B' : 'rgba(255,255,255,0.18)'}>
                  <path d={STAR_PATH} />
                </svg>
              </button>
            ))}
          </div>

          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder="Napisz, co sądzisz o produkcie (opcjonalnie)…"
            className="w-full px-4 py-3 rounded-xl text-sm outline-none focus:ring-2 resize-y"
            style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', color: '#FFFFFF' }}
          />

          {msg && (
            <p className="text-sm mt-3" style={{ color: msg.type === 'ok' ? '#34D399' : '#F87171' }}>
              {msg.text}
            </p>
          )}

          <div className="flex items-center gap-3 mt-4">
            <button
              type="submit"
              disabled={pending}
              className="px-6 py-3 rounded-xl font-semibold text-sm transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#FF6B47', color: '#03080F' }}
            >
              {pending ? 'Zapisywanie…' : editing ? 'Zaktualizuj opinię' : 'Dodaj opinię'}
            </button>
            {editing && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={pending}
                className="text-sm font-medium hover:underline disabled:opacity-50"
                style={{ color: 'rgba(255,255,255,0.5)' }}
              >
                Usuń opinię
              </button>
            )}
          </div>
        </form>
      )}

      {!unavailable && loggedIn === false && (
        <div
          className="rounded-2xl p-6 mb-8 text-sm"
          style={{ backgroundColor: '#0D1E30', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}
        >
          <Link href="/login" className="font-semibold hover:underline" style={{ color: '#1FB8B0' }}>
            Zaloguj się
          </Link>
          , aby dodać opinię o tym produkcie.
        </div>
      )}

      {/* Lista opinii */}
      {reviews.length > 0 && (
        <ul className="space-y-5">
          {reviews.map((r) => (
            <li key={r.id} className="pb-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-white text-sm">{r.author}</span>
                  {r.id === ownReviewId && (
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(31,184,176,0.12)', color: '#1FB8B0' }}>
                      Twoja opinia
                    </span>
                  )}
                </div>
                <span className="text-xs flex-shrink-0" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  {formatDate(r.createdAt)}
                </span>
              </div>
              <Stars value={r.rating} />
              {r.comment && (
                <p className="text-sm mt-2 leading-relaxed whitespace-pre-line" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {r.comment}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
